import {
  getWebInstrumentations,
  initializeFaro,
  LogLevel,
  type Faro,
} from '@grafana/faro-web-sdk';
import type { ConfigFacade } from '../config/config.facade';
import { useGlobalDependencyProvider } from '../di/useGlobalDependencyProvider';
import type { ObservabilityFacade } from './observability.facade';

export class FaroObservabilityService implements ObservabilityFacade {
  private config: ConfigFacade;
  private faro?: Faro;

  /** @param appVersion the release this build belongs to, e.g. `v2.1.24` */
  constructor(appVersion: string) {
    const di = useGlobalDependencyProvider();
    this.config = di.inject('config');
    const url = this.config.observabilityUrl();
    const environment = this.config.observabilityEnvironment();
    const isActive = this.config.isObservabilityActive();
    const apiKey = this.config.observabilityApiKey();

    if (!isActive) {
      console.info('Observability not active in this environment');
      return;
    }

    this.faro = initializeFaro({
      url,
      apiKey,
      instrumentations: getWebInstrumentations({
        captureConsole: true,
        enablePerformanceInstrumentation: true,
      }),
      consoleInstrumentation: {
        consoleErrorAsLog: true,
        disabledLevels: this.configureConsoleCaptureDisabledLevels(),
      },
      app: {
        name: 'frontend',
        version: appVersion,
        environment,
      },
    });
  }
  setUser(id: string) {
    this.faro?.api.setUser({ id });
  }

  /** Tags every later signal with `view_name`; pass a route pattern, never a URL. */
  setView(name: string): void {
    this.faro?.api.setView({ name });
  }

  private configureConsoleCaptureDisabledLevels(): LogLevel[] {
    const observabilityProfile = this.config.observabilityProfile();

    const prodDisabledConsoleLevels = [
      LogLevel.TRACE,
      LogLevel.DEBUG,
      LogLevel.INFO,
      LogLevel.LOG,
      LogLevel.WARN,
    ];
    const disabledConsoleLevelMap = new Map<string, LogLevel[]>([
      ['dev', []],
      ['prod', prodDisabledConsoleLevels],
    ]);

    let captureConsoleDisabledLevels =
      disabledConsoleLevelMap.get(observabilityProfile);
    if (!captureConsoleDisabledLevels) {
      console.warn(
        `invalid observability profile, given: "${observabilityProfile}", falling back to prod profile `,
      );
      captureConsoleDisabledLevels = prodDisabledConsoleLevels;
    }
    return captureConsoleDisabledLevels;
  }
}
